/**
 * Gift card value rules, shared by every place staff can load a card.
 *
 * The server enforces the limits: `POST /gift-cards`,
 * `GiftCardsController::reload` and the gift-card SKU path in
 * `PatientPaymentService` check every amount against the location's Gift Card
 * Settings (GiftCardSettingsService, answering GIFT_CARD_AMOUNT_OUT_OF_RANGE),
 * whose ceiling is GiftCardSettingsService::MAX_AMOUNT. The cap below mirrors
 * that ceiling so the entry points — the Issue Gift Card modal, the gift-card
 * product line in the payment modals, and Add Balance on the gift card detail
 * modal — can stop an over-limit amount before it is sent. A location's own
 * lower maximum is only known to the server; its error message is shown.
 */

import { getErrorCode, getErrorMessage } from '@/services/apiClient';

/** Maximum value allowed on a single gift card. */
export const MAX_GIFT_CARD_AMOUNT = 99999;

/**
 * Clamp a card's loaded value into the allowed range. For number-backed
 * fields; string-backed inputs compare against MAX_GIFT_CARD_AMOUNT directly
 * so a half-typed value ("0.5" on the way to "0.50") is kept verbatim.
 */
export function clampGiftCardAmount(value: number): number {
  if (!Number.isFinite(value) || value < 0) return 0;
  return Math.min(value, MAX_GIFT_CARD_AMOUNT);
}

/**
 * How much more may be loaded onto a card already holding `currentBalance`.
 *
 * Add Balance ADDS to the balance (`GiftCardsController::reload` writes
 * `balanceAmount + amount`), so capping the entered amount on its own would
 * still let a card past the maximum — a $60,000 card plus a $99,999 reload is
 * a $159,999 card. The cap belongs on the resulting balance, which makes the
 * limit on the entry field the card's remaining headroom.
 *
 * Returns 0 for a card already at or over the cap.
 */
export function giftCardReloadHeadroom(currentBalance: number): number {
  const balance = Number.isFinite(currentBalance) ? Math.max(currentBalance, 0) : 0;
  return Math.max(MAX_GIFT_CARD_AMOUNT - balance, 0);
}

/**
 * What to tell staff when a typed gift card code is refused at the desk.
 *
 * The API applies the card rules for the patient paying (R12, R13): another
 * patient's card answers 409 GIFT_CARD_OWNED_BY_OTHER_PATIENT, too many wrong
 * codes 429 GIFT_CARD_CODE_LOCKED. Cards are looked up inside this location's
 * account only, so a card sold at another location is simply not found
 * (404 NOT_FOUND / GIFT_CARD_NOT_FOUND) and the message says where to look.
 */
export function giftCardCodeErrorMessage(error: unknown, fallback = 'Failed to look up gift card.'): string {
  switch (getErrorCode(error)) {
    case 'GIFT_CARD_OWNED_BY_OTHER_PATIENT':
      return 'This gift card belongs to another patient.';
    case 'GIFT_CARD_CODE_LOCKED':
      return 'Too many wrong gift card codes. Try again in a few minutes.';
    case 'NOT_FOUND':
    case 'GIFT_CARD_NOT_FOUND':
      return 'Gift card not found at this location. A card can only be used at the location that sold it.';
    case 'GIFT_CARD_NO_BALANCE':
      return 'This gift card has no balance left.';
    default:
      return getErrorMessage(error, fallback);
  }
}
